import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  type Timestamp,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { apiFetch } from '../lib/api';
import type { Shipment, Market } from '../types';

export type ShipmentFilter = 'active' | 'awaiting_fulfillment' | 'in_transit' | 'at_risk' | 'needs_action' | 'deliver_today' | 'delivered_today' | 'delivered_yesterday' | 'exceptions';

// Convert Firestore timestamps to Dates
function convertTimestamps(data: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(data)) {
    if (value && typeof value === 'object') {
      if ('toDate' in value && typeof (value as Timestamp).toDate === 'function') {
        result[key] = (value as Timestamp).toDate();
      } else if (Array.isArray(value)) {
        result[key] = value.map((item) =>
          typeof item === 'object' ? convertTimestamps(item as Record<string, unknown>) : item
        );
      } else {
        result[key] = convertTimestamps(value as Record<string, unknown>);
      }
    } else {
      result[key] = value;
    }
  }

  return result;
}

// Convert date strings from API to Date objects
function convertApiDates(shipment: Record<string, unknown>): Shipment {
  const dateFields = [
    'expected_ship_date',
    'deadline',
    'last_tracking_update',
    'tracking_refreshed_at',
    'delivered_at',
    'picked_up_at',
    'created_at',
    'updated_at',
  ];

  const result = { ...shipment };
  for (const field of dateFields) {
    if (result[field] && typeof result[field] === 'string') {
      result[field] = new Date(result[field] as string);
    }
  }

  // Convert customer.customer_since if present
  if (result.customer && typeof result.customer === 'object') {
    const customer = result.customer as Record<string, unknown>;
    if (customer.customer_since && typeof customer.customer_since === 'string') {
      customer.customer_since = new Date(customer.customer_since as string);
    }
  }

  return result as unknown as Shipment;
}

// Get API base URL from environment
// Empty string = same origin (Firebase Hosting proxies to Cloud Run)
const API_BASE_URL = import.meta.env.VITE_API_URL ?? '';

// Fetch delivered shipments from API
async function fetchDeliveredFromApi(
  period: 'today' | 'yesterday',
  market: Market | 'all'
): Promise<Shipment[]> {
  const url = market !== 'all'
    ? `${API_BASE_URL}/api/shipments/delivered/${period}?market=${market}`
    : `${API_BASE_URL}/api/shipments/delivered/${period}`;

  const response = await apiFetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch delivered shipments: ${response.statusText}`);
  }

  const json = await response.json();
  if (!json.success) {
    throw new Error(json.error || 'Failed to fetch delivered shipments');
  }

  return (json.data as Record<string, unknown>[]).map(convertApiDates);
}

export function useShipments(
  market: Market | 'all' = 'all',
  filter: ShipmentFilter = 'active'
) {
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setShipments([]); // Clear shipments when filter changes

    // For delivered filters, fetch from API instead of Firestore
    if (filter === 'delivered_today' || filter === 'delivered_yesterday') {
      const period = filter === 'delivered_today' ? 'today' : 'yesterday';

      fetchDeliveredFromApi(period, market)
        .then((data) => {
          setShipments(data);
          setLoading(false);
        })
        .catch((err) => {
          console.error('API error:', err);
          setError(err.message);
          setLoading(false);
        });

      // No cleanup needed for API fetch
      return;
    }

    // Build query based on filter
    let q;

    if (filter === 'active') {
      // Active shipments: not delivered, ordered by deadline
      if (market !== 'all') {
        q = query(
          collection(db, 'shipments'),
          where('market', '==', market),
          where('is_delivered', '==', false),
          orderBy('deadline')
        );
      } else {
        q = query(
          collection(db, 'shipments'),
          where('is_delivered', '==', false),
          orderBy('deadline')
        );
      }
    } else if (filter === 'awaiting_fulfillment') {
      // Awaiting fulfillment: not yet shipped (includes 'pending' for Loomis awaiting pickup)
      const awaitingStatuses = ['awaiting_fulfillment', 'pending'];
      if (market !== 'all') {
        q = query(
          collection(db, 'shipments'),
          where('market', '==', market),
          where('status', 'in', awaitingStatuses),
          orderBy('deadline')
        );
      } else {
        q = query(
          collection(db, 'shipments'),
          where('status', 'in', awaitingStatuses),
          orderBy('deadline')
        );
      }
    } else if (filter === 'in_transit') {
      // In transit: shipped but not delivered (excludes awaiting_fulfillment, pending, and exceptions)
      const transitStatuses = ['picked_up', 'in_transit', 'out_for_delivery'];
      if (market !== 'all') {
        q = query(
          collection(db, 'shipments'),
          where('market', '==', market),
          where('status', 'in', transitStatuses),
          orderBy('deadline')
        );
      } else {
        q = query(
          collection(db, 'shipments'),
          where('status', 'in', transitStatuses),
          orderBy('deadline')
        );
      }
    } else if (filter === 'at_risk') {
      // At risk: high or critical risk level, not delivered
      const riskLevels = ['high', 'critical'];
      if (market !== 'all') {
        q = query(
          collection(db, 'shipments'),
          where('market', '==', market),
          where('is_delivered', '==', false),
          where('risk_level', 'in', riskLevels),
          orderBy('deadline')
        );
      } else {
        q = query(
          collection(db, 'shipments'),
          where('is_delivered', '==', false),
          where('risk_level', 'in', riskLevels),
          orderBy('deadline')
        );
      }
    } else if (filter === 'needs_action') {
      // Needs action: has recommended actions, not delivered
      if (market !== 'all') {
        q = query(
          collection(db, 'shipments'),
          where('market', '==', market),
          where('is_delivered', '==', false),
          where('needs_action', '==', true),
          orderBy('deadline')
        );
      } else {
        q = query(
          collection(db, 'shipments'),
          where('is_delivered', '==', false),
          where('needs_action', '==', true),
          orderBy('deadline')
        );
      }
    } else if (filter === 'deliver_today') {
      // Deliver today: deadline is today or overdue, not delivered
      const tomorrow = new Date();
      tomorrow.setHours(0, 0, 0, 0);
      tomorrow.setDate(tomorrow.getDate() + 1);

      if (market !== 'all') {
        q = query(
          collection(db, 'shipments'),
          where('market', '==', market),
          where('is_delivered', '==', false),
          where('deadline', '<', tomorrow),
          orderBy('deadline')
        );
      } else {
        q = query(
          collection(db, 'shipments'),
          where('is_delivered', '==', false),
          where('deadline', '<', tomorrow),
          orderBy('deadline')
        );
      }
    } else if (filter === 'exceptions') {
      // Exceptions: shipments with exception status
      if (market !== 'all') {
        q = query(
          collection(db, 'shipments'),
          where('market', '==', market),
          where('status', '==', 'exception'),
          orderBy('deadline')
        );
      } else {
        q = query(
          collection(db, 'shipments'),
          where('status', '==', 'exception'),
          orderBy('deadline')
        );
      }
    } else {
      // Fallback: all shipments (shouldn't reach here)
      q = query(
        collection(db, 'shipments'),
        orderBy('deadline')
      );
    }

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...convertTimestamps(doc.data()),
        })) as Shipment[];
        setShipments(data);
        setLoading(false);
      },
      (err) => {
        console.error('Firestore error:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [market, filter]);

  return { shipments, loading, error };
}

export function useShipment(id: string) {
  const [shipment, setShipment] = useState<Shipment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setShipment(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    const unsubscribe = onSnapshot(
      collection(db, 'shipments'),
      (snapshot) => {
        const doc = snapshot.docs.find((d) => d.id === id);
        if (doc) {
          setShipment({
            id: doc.id,
            ...convertTimestamps(doc.data()),
          } as Shipment);
        } else {
          setShipment(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Firestore error:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [id]);

  return { shipment, loading, error };
}
